import dayjs from 'dayjs'
import { CellColor } from 'pages/app/billing/invoices/invoices-table/cell/types.ts'
import i18next from 'i18next'
import { StatusProps, StatusReturnValue } from 'pages/app/billing/invoices/invoices-table/types.ts'
import { isCustomerInvoice, isCustomerPayment } from 'pages/app/billing/invoices/utils.ts'
import { DATE_FORMAT } from 'constants/constants.ts'

/**
 * Returns the status as variant and text value, for a given document
 *
 * @param {StatusProps} props
 */
export const getStatus = ({ document, usesDirectDebit }: StatusProps): StatusReturnValue => {
  // Constants
  const isInvoice = isCustomerInvoice(document)
  const isPayment = isCustomerPayment(document)
  const isDebtSold = isInvoice && document.debtSold
  const today = dayjs()
  const invoiceDueDate = isInvoice && !!document?.dueDate ? dayjs(document.dueDate) : undefined

  // ORDER IS IMPORTANT
  // ------------------

  // 0. Handle debt booking/sold
  if (isDebtSold) {
    return {
      color: CellColor.DEFAULT,
      value: i18next.t(($) => $.status.debtSold, {
        ns: 'invoices'
      })
    }
  }

  // 1. Reminder fees
  if (isPayment && document.lineType === 90) {
    return {
      color: CellColor.DEFAULT,
      value: i18next.t(($) => $.status.reminderFee, {
        ns: 'invoices'
      })
    }
  }

  // 2. Payments
  if (isPayment) {
    return {
      color: CellColor.DEFAULT,
      value:
        document.amount < 0
          ? i18next.t(($) => $.status.boltRecieved, {
              ns: 'invoices'
            })
          : i18next.t(($) => $.status.boltPayed, {
              ns: 'invoices'
            })
    }
  }

  // 3. 'Bolt has received this amount' / 'Bolt has paid this amount'
  if (document.amount < 0 && document.paid) {
    return {
      color: CellColor.POSITIVE,
      value: i18next.t(($) => $.status.boltPayed, {
        ns: 'invoices'
      })
    }
  }

  // 4. “Will be refunded to you”
  if (document.amount < 0 && !document.paid) {
    return {
      color: CellColor.POSITIVE,
      value: i18next.t(($) => $.status.willBeRefunded, {
        ns: 'invoices'
      })
    }
  }

  // 5. 'Paid' / 'Bolt has paid this amount'
  if (document.paid) {
    return {
      color: CellColor.POSITIVE,
      value: i18next.t(($) => $.status.paid, {
        ns: 'invoices'
      })
    }
  }

  // 6. 'Direct debit failed'
  if (isInvoice && !!invoiceDueDate && today.isAfter(invoiceDueDate.add(4, 'days'), 'day') && usesDirectDebit) {
    return {
      color: CellColor.NEGATIVE,
      value: i18next.t(($) => $.status.directDebitFailed, {
        ns: 'invoices'
      })
    }
  }

  // 7. 'Payable at XX/XX/XXXX' - bank transfer overdue
  if (isInvoice && !!invoiceDueDate && today.isAfter(invoiceDueDate, 'day') && !usesDirectDebit) {
    return {
      color: CellColor.NEGATIVE,
      value: `${i18next.t(($) => $.status.paymentDue, {
        ns: 'invoices'
      })} ${invoiceDueDate.format(DATE_FORMAT)}`
    }
  }

  // 8. 'Payable at XX/XX/XXXX' - directDebit approaching
  if (isInvoice && !!invoiceDueDate && invoiceDueDate.add(4, 'days').isAfter(today, 'day') && usesDirectDebit) {
    return {
      color: CellColor.INFORMATIVE,
      value: `${i18next.t(($) => $.status.directDebitDue, {
        ns: 'invoices'
      })} ${invoiceDueDate.format(DATE_FORMAT)}`
    }
  }

  // 9. 'Payable at XX/XX/XXXX' - bank transfer approaching
  if (isInvoice && !!invoiceDueDate && invoiceDueDate.isSameOrAfter(today, 'day') && !usesDirectDebit) {
    return {
      color: CellColor.WARNING,
      value: `${i18next.t(($) => $.status.paymentDue, {
        ns: 'invoices'
      })} ${invoiceDueDate.format(DATE_FORMAT)}`
    }
  }

  return {
    color: undefined,
    value: ''
  }
}
